import {z} from "zod";

/**
 * Currencies whose minor unit is not 1/100. Anything absent uses two decimals,
 * which matches the overwhelming majority of ISO 4217 codes.
 */
const exponentOverrides: Record<string, number> = {
  BIF: 0,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  ISK: 0,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  PYG: 0,
  RWF: 0,
  UGX: 0,
  UYI: 0,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XOF: 0,
  XPF: 0,
  BHD: 3,
  IQD: 3,
  JOD: 3,
  KWD: 3,
  LYD: 3,
  OMR: 3,
  TND: 3
};

export function currencyExponent(currency: string): number {
  return exponentOverrides[currency.toUpperCase()] ?? 2;
}

function scaleFor(currency: string): number {
  return 10 ** currencyExponent(currency);
}

/**
 * Money is stored and computed in integer minor units. Floating point is only
 * ever introduced at the moment of display, so no sum can drift.
 */
export const moneySchema = z.object({
  minorUnits: z.number().int(),
  currency: z.string().regex(/^[A-Z]{3}$/)
});
export type Money = z.infer<typeof moneySchema>;

export function money(minorUnits: number, currency: string): Money {
  const rounded = Math.round(minorUnits);
  if (!Number.isSafeInteger(rounded)) throw new Error("INVALID_MONEY_AMOUNT");
  return {minorUnits: rounded === 0 ? 0 : rounded, currency: currency.toUpperCase()};
}

/** Converts a human-entered major amount, such as 12.34, into minor units. */
export function fromMajorUnits(amount: number, currency: string): Money {
  if (!Number.isFinite(amount)) throw new Error("INVALID_MONEY_AMOUNT");
  // Round the decimal representation, so binary floating-point artifacts do
  // not turn 1.005 into 100 cents or round refunds differently from charges.
  const [decimal = "0", exponent = "0"] = Math.abs(amount).toString().split("e");
  const [whole = "0", fraction = ""] = decimal.split(".");
  const shift = Number(exponent) + currencyExponent(currency) - fraction.length;
  const digits = BigInt(whole + fraction);
  const divisor = 10n ** BigInt(Math.max(0, -shift));
  const minor = shift >= 0 ? digits * 10n ** BigInt(shift) : digits / divisor + ((digits % divisor) * 2n >= divisor ? 1n : 0n);
  return money(Number(minor) * (amount < 0 ? -1 : 1), currency);
}

export function toMajorUnits(value: Money): number {
  return value.minorUnits / scaleFor(value.currency);
}

export function zeroMoney(currency: string): Money {
  return money(0, currency);
}

function assertSameCurrency(a: Money, b: Money): void {
  if (a.currency !== b.currency) throw new Error(`CURRENCY_MISMATCH_${a.currency}_${b.currency}`);
}

export function addMoney(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return money(a.minorUnits + b.minorUnits, a.currency);
}

export function subtractMoney(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return money(a.minorUnits - b.minorUnits, a.currency);
}

export function negateMoney(value: Money): Money {
  return money(-value.minorUnits, value.currency);
}

export function absMoney(value: Money): Money {
  return money(Math.abs(value.minorUnits), value.currency);
}

export function isZero(value: Money): boolean {
  return value.minorUnits === 0;
}

export function compareMoney(a: Money, b: Money): number {
  assertSameCurrency(a, b);
  return a.minorUnits - b.minorUnits;
}

/** Totals a single-currency list. Mixed currencies must use `sumByCurrency`. */
export function sumMoney(values: readonly Money[], currency: string): Money {
  return values.reduce<Money>((total, value) => addMoney(total, value), zeroMoney(currency));
}

export function sumByCurrency(values: readonly Money[]): Money[] {
  const totals = new Map<string, Money>();
  for (const value of values) totals.set(value.currency, addMoney(totals.get(value.currency) ?? zeroMoney(value.currency), value));
  return [...totals.values()].sort((a, b) => a.currency.localeCompare(b.currency));
}

export function formatMoney(value: Money): string {
  const exponent = currencyExponent(value.currency);
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: value.currency,
      minimumFractionDigits: exponent,
      maximumFractionDigits: exponent
    }).format(toMajorUnits(value));
  } catch {
    return `${toMajorUnits(value).toFixed(exponent)} ${value.currency}`;
  }
}

/** Ratio of two amounts in the same currency, safe when the base is zero. */
export function ratio(value: Money, base: Money): number | null {
  assertSameCurrency(value, base);
  if (base.minorUnits === 0) return null;
  return value.minorUnits / base.minorUnits;
}
